import { useState, useEffect } from 'react';
import { Box, Text, Pagination, Loader, Center, Group, Button, Badge, ActionIcon } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconRefresh, IconPlus, IconSettings, IconTrash } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useBot } from '@mygpt/app/bot';
import {
  getBotMcpServices,
  getMcpConfig,
  createBotMcpConfig,
  updateMcpConfig,
  deleteMcpConfig,
  toggleBotMcpService,
  type BotMcpService,
  type CreateMcpConfigRequest,
} from '@mygpt/app/apis';
import { McpSettingsDialog } from '@mygpt/app/mcp';

const MCP = () => {
  const { t } = useTranslation();
  const { botId } = useBot();
  const [services, setServices] = useState<BotMcpService[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editingLoading, setEditingLoading] = useState<string | null>(null);
  const [deletingService, setDeletingService] = useState<string | null>(null);
  const [mcpDialogOpen, setMcpDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<BotMcpService | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    size: 10,
    total: 0,
    pages: 0,
  });

  console.log('MCP Component mounted, botId:', botId);

  // 判断是否是bot级别的MCP（可编辑删除）
  const isBotOwnedMcp = (service: BotMcpService) => {
    return service.scope === 'bot_level';
  };

  const loadMCPServices = async (page = 1) => {
    console.log('loadMCPServices called, page:', page, 'botId:', botId);
    if (!botId) {
      console.log('No botId, returning');
      return;
    }

    try {
      setLoading(true);
      const response = await getBotMcpServices(botId, {
        page,
        size: pagination.size,
      });

      setServices(response.items);
      setPagination((prev) => ({
        ...prev,
        page: response.page,
        total: response.total,
        pages: response.pages,
      }));
    } catch (error) {
      showNotification({
        message: t('mcp.loadError'),
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddMcpService = () => {
    setEditingService(null);
    setMcpDialogOpen(true);
  };

  const handleEditMcpService = async (service: BotMcpService) => {
    // 只有bot级别的MCP才能编辑
    if (!isBotOwnedMcp(service)) {
      return;
    }

    try {
      setEditingLoading(service.id);
      // 调用详情接口获取完整的MCP配置
      const mcpDetail = await getMcpConfig(service.id);
      // 将详情数据转换为BotMcpService格式
      const fullService: BotMcpService = {
        ...service,
        json_config: mcpDetail.json_config,
        type: mcpDetail.type,
      };
      setEditingService(fullService);
      setMcpDialogOpen(true);
    } catch (err) {
      console.error('failed to get mcp config detail:', err);
    } finally {
      setEditingLoading(null);
    }
  };

  const handleSaveMcpService = async (serviceData: {
    name: string;
    description: string;
    available_tools: string;
    jsonConfig: string;
    type: number;
  }): Promise<boolean> => {
    if (!botId) return false;

    try {
      const configData = {
        name: serviceData.name,
        description: serviceData.description,
        available_tools: serviceData.available_tools,
        json_config: JSON.parse(serviceData.jsonConfig),
        type: serviceData.type,
      };

      if (editingService) {
        // 编辑现有服务：显式回传当前启用状态，避免后端 default=1 静默覆盖
        // （不传 is_enabled 会触发后端默认值 1，把禁用中的 MCP 强制启用）
        await updateMcpConfig(editingService.id, {
          ...configData,
          is_enabled: editingService.is_enabled_for_bot ? 1 : 0,
        });

        // 编辑后不刷新列表，只更新本地状态
        setServices((prevServices) =>
          prevServices.map((s) =>
            s.id === editingService.id
              ? {
                  ...s,
                  name: serviceData.name,
                  description: serviceData.description,
                  available_tools: serviceData.available_tools,
                  type: serviceData.type,
                }
              : s
          )
        );

        showNotification({
          message: t('mcp.editSuccess'),
          color: 'green',
        });
      } else {
        // 添加新服务
        await createBotMcpConfig(botId, {
          ...configData,
          is_enabled: 0, // 新建时默认禁用
        });

        // 新建后刷新列表
        await loadMCPServices(pagination.page);

        showNotification({
          message: t('mcp.createSuccess'),
          color: 'green',
        });
      }

      setMcpDialogOpen(false);
      return true;
    } catch (err: any) {
      console.error('failed to save mcp config:', err);
      // 保存失败需提示用户（如名称超长致 422），不静默吞错
      const detail = err?.response?.data?.detail;
      showNotification({
        color: 'red',
        message: detail || t('errors.common'),
      });
      return false;
    }
  };

  const handleDeleteMcpService = (service: BotMcpService) => {
    // 只有bot级别的MCP才能删除
    if (!isBotOwnedMcp(service)) {
      return;
    }

    if (deletingService) return;

    setDeletingService(service.id);

    modals.openConfirmModal({
      title: t('mcp.delete.title'),
      children: (
        <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
          {t('mcp.delete.content', { name: service.name })}
        </Text>
      ),
      labels: {
        confirm: t('mcp.delete.confirm'),
        cancel: t('mcp.delete.cancel'),
      },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteMcpConfig(service.id);
          showNotification({
            message: t('mcp.deleteSuccess'),
            color: 'green',
          });

          // 重新加载数据
          const shouldGoToPrevPage = services.length === 1 && pagination.page > 1;
          const targetPage = shouldGoToPrevPage ? pagination.page - 1 : pagination.page;
          await loadMCPServices(targetPage);
        } catch (err) {
          console.error('failed to delete mcp config:', err);
        }
        setDeletingService(null);
      },
      onCancel: () => {
        setDeletingService(null);
      },
      onClose: () => {
        setDeletingService(null);
      },
      centered: true,
    });
  };

  const handleServiceToggle = async (service: BotMcpService) => {
    if (!botId) return;

    try {
      setActionLoading(service.id);
      const newEnabled = !service.is_enabled_for_bot;

      const response = await toggleBotMcpService(botId, service.id, newEnabled);

      // 更新本地状态，使用API返回的数据
      setServices((prevServices) =>
        prevServices.map((s) =>
          s.id === service.id
            ? {
                ...s,
                is_enabled_for_bot: response.is_enabled,
                enabled_at: response.enabled_at,
              }
            : s
        )
      );

      showNotification({
        message: newEnabled ? t('mcp.enableSuccess') : t('mcp.disableSuccess'),
        color: 'green',
      });
    } catch (error) {
      showNotification({
        message: t('mcp.toggleError'),
        color: 'red',
      });
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    console.log('useEffect triggered, botId:', botId);
    if (botId) {
      console.log('Calling loadMCPServices from useEffect');
      void loadMCPServices(pagination.page);
    }
  }, [botId]);

  if (loading) {
    return (
      <Center h={200}>
        <Loader size="md" />
      </Center>
    );
  }

  return (
    <Box>
      <div className="space-y-6">
        {/* Header with action buttons */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Text size="sm" className="text-gray-900 mb-2">
              {t('mcp.title')}
            </Text>
            <Text size="sm" className="text-gray-600">
              {t('mcp.description')}
            </Text>
          </div>
          <Group gap="sm">
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={() => loadMCPServices(pagination.page)}
              loading={loading}
              size="sm"
            >
              {t('common.refresh')}
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAddMcpService} size="sm">
              {t('mcp.add_service')}
            </Button>
          </Group>
        </div>

        {services.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <Text c="dimmed">{t('mcp.noServices')}</Text>
          </div>
        ) : (
          services.map((service) => {
            const isOwned = isBotOwnedMcp(service);
            return (
              <div key={service.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <Group gap="sm" mb={4}>
                      <Text size="sm" className="text-gray-900">
                        {service.name}
                      </Text>
                      {/* 公用MCP显示品牌标识 */}
                      {!isOwned && (
                        <Badge size="sm" color="violet" variant="light" tt="none">
                          GBase Support
                        </Badge>
                      )}
                    </Group>
                    <Text size="sm" className="text-gray-600 mb-2">
                      {service.description}
                    </Text>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    {/* 编辑按钮 - 只有自建的才显示 */}
                    {isOwned && (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={() => handleEditMcpService(service)}
                        title={t('mcp.actions.edit')}
                        size="sm"
                        loading={editingLoading === service.id}
                        disabled={editingLoading === service.id}
                      >
                        <IconSettings size={16} />
                      </ActionIcon>
                    )}

                    {/* 删除按钮 - 只有自建的才显示 */}
                    {isOwned && (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleDeleteMcpService(service)}
                        title={t('mcp.actions.delete')}
                        size="sm"
                        disabled={deletingService !== null}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    )}

                    {/* Toggle Switch */}
                    <button
                      onClick={() => handleServiceToggle(service)}
                      className={`w-12 h-7 rounded-full border flex items-center transition-all duration-200 ${
                        service.is_enabled_for_bot ? 'bg-green-500 border-green-500' : 'bg-gray-300 border-gray-300'
                      }`}
                      title={service.is_enabled_for_bot ? t('mcp.actions.disable') : t('mcp.actions.enable')}
                      disabled={actionLoading === service.id}
                    >
                      <div
                        className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                          service.is_enabled_for_bot ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>

                    {/* Connection Status Indicator */}
                    <div
                      className={`w-4 h-4 rounded-full transition-colors ${
                        service.is_enabled_for_bot ? 'bg-green-500' : 'bg-red-500'
                      }`}
                      title={service.is_enabled_for_bot ? t('mcp.status.connected') : t('mcp.status.disconnected')}
                    />
                  </div>
                </div>

                <div>
                  <Text size="sm" className="text-gray-700 mb-1">
                    {t('mcp.availableTools')}:
                  </Text>
                  <div className="flex flex-wrap gap-2">
                    {service.available_tools ? (
                      service.available_tools.split(',').map((tool, index) => (
                        <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                          {tool.trim()}
                        </span>
                      ))
                    ) : (
                      <Text size="sm" className="text-gray-600">
                        {t('mcp.noTools')}
                      </Text>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* Pagination */}
        {pagination.total > 0 && pagination.pages > 1 && (
          <Group justify="center" mt="lg">
            <Pagination
              value={pagination.page}
              total={pagination.pages}
              onChange={(page) => loadMCPServices(page)}
              size="sm"
              withEdges
            />
          </Group>
        )}
      </div>

      {/* Settings Dialog */}
      <McpSettingsDialog
        opened={mcpDialogOpen}
        onClose={() => {
          setMcpDialogOpen(false);
          setEditingService(null);
        }}
        editingService={editingService}
        onSave={handleSaveMcpService}
      />
    </Box>
  );
};

export default MCP;
